import { toast } from 'react-toastify';
import { put, all, fork, select, call, takeLeading, debounce, takeLatest } from 'redux-saga/effects';
import { isEqual, isNull } from 'lodash/fp';
import { push } from 'redux-first-history';
import download from 'downloadjs';

import { fetchAllAgreementTypes, fetchAllAgreementTypeRelationships, fetchAgreementTypeHierarchy, fetchAllDocumentGroups, fetchAllDocumentNames, fetchAvailableDocumentNames, updateAgreementTypes, updateDocumentName, upsertClientDocumentGroup, deleteClientDocumentGroup, fetchMLReadyAgreementTypeIds, fetchAllAdminDocumentNames, upsertAgreementTypeHierarchy } from '../../../../services/document';
import { Client } from '../../clients/store/types';
import { getAllClients } from '../../clients/store/selectors';
import { fetchAllAgreementTypesFailed, fetchAllAgreementTypesSuccessful, fetchAllDocumentNamesFailed, fetchAllDocumentNamesSuccessful, fetchAvailableDocumentNamesFailed, fetchAvailableDocumentNamesSuccessful, fetchMLReadyAgreementTypeIdsFailed, fetchMLReadyAgreementTypeIdsSuccessful, setAgreementTypesHaveUpdated, toggleClientDatasetConfirmationModal, updateAgreementTypesFailed, updateAgreementTypesSuccessful, updateDocumentNameFailed, updateDocumentNameSuccessful, updateValue, fetchAllDocumentGroupsFailed, fetchAllDocumentGroupsSuccessful, saveClientDocumentGroupFailed, saveClientDocumentGroupSuccessful, fetchAllDocumentGroupsStarted, deleteClientDocumentGroupFailed, deleteClientDocumentGroupSuccessful, fetchAllAdminDocumentNamesSuccessful, fetchAllAdminDocumentNamesFailed, setSystemDocumentNameUpdated, fetchAllAgreementTypeRelationshipsSuccessful, fetchAllAgreementTypeRelationshipsFailed, saveAgreementTypeHierarchySuccessful, saveAgreementTypeHierarchyFailed, setCanSaveAgreementHierarchy, setAgreementRelationshipId, fetchAgreementTypeHierarchyStarted, fetchAgreementTypeHierarchySuccessful, fetchAgreementTypeHierarchyFailed, setDocumentNamePageView, generateReportFailed, generateReportStarted, generateReportSuccessful } from './actions';
import { getAgreementTypesHaveUpdated, getAllAgreementTypes, getCurrentAgreementHierarchy, getCurrentAgreementTypes, getCurrentDocumentName, getSavedAgreementHierarchy, getSavedDocumentName, getSelectedAgreementTypeId, getSelectedDocumentGroup } from './selectors';
import { DocumentNameDB, DocumentNameActionTypes, ClientDocumentGroup, AgreementTypeDB, CurrentAgreementType, SystemDocumentNameDB, AgreementTypeRelationship, AgreementTypeHierarchy, DocumentNamePageView, AgreementHierarchyRank } from './types';
import { createPopulatedFieldsReport } from '../../../../services/report';
import { MimeType } from '../../../constants/MimeType';

export function* attemptUpdateDocumentName() {
    try {
        const documentName: SystemDocumentNameDB = yield select(getCurrentDocumentName);
        const documentNames: SystemDocumentNameDB[] = yield call(updateDocumentName, documentName);
        yield put(updateDocumentNameSuccessful(documentNames));
        toast('Successfully updated document.');
    } catch (e) {
        yield put(updateDocumentNameFailed((e as Error).message));
        toast.error('Unable to save the document. Please try again.');
    }
}

export function* attemptFetchAllDocumentNames() {
    try {
        const documentNames: DocumentNameDB[] = yield call(fetchAllDocumentNames);
        yield put(fetchAllDocumentNamesSuccessful(documentNames));
    } catch (e) {
        yield put(fetchAllDocumentNamesFailed((e as Error).message));
        toast.error('Unable to fetch the latest list of document names. Please try again.');
    }
}

function* updateDocumentNameWatcher() {
    yield takeLeading(DocumentNameActionTypes.UPDATE_DOCUMENT_NAME_STARTED, attemptUpdateDocumentName);
}

function* fetchAllDocumentNamesWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_ALL_DOCUMENT_NAMES_STARTED, attemptFetchAllDocumentNames);
}

export function* attemptFetchAllAgreementTypes() {
    try {
        const agreementTypes: AgreementTypeDB[] = yield call(fetchAllAgreementTypes);
        yield put(fetchAllAgreementTypesSuccessful(agreementTypes));
    } catch (e) {
        yield put(fetchAllAgreementTypesFailed((e as Error).message));
        toast.error('Unable to fetch the latest list of agreement types. Please try again.');
    }
}

function* fetchAllAgreementTypesWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_ALL_AGREEMENT_TYPES_STARTED, attemptFetchAllAgreementTypes);
}

export function* attemptFetchAvailableDocumentNames() {
    try {
        const documentNames: DocumentNameDB[] = yield call(fetchAvailableDocumentNames);
        yield put(fetchAvailableDocumentNamesSuccessful(documentNames));
    } catch (e) {
        yield put(fetchAvailableDocumentNamesFailed((e as Error).message));
        toast.error('Unable to fetch the latest list of document names. Please try again.');
    }
}

function* fetchAvailableDocumentNamesWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_AVAILABLE_DOCUMENT_NAMES_STARTED, attemptFetchAvailableDocumentNames);
}

export function* updateDocumentNameClientIds({ payload }: ReturnType<typeof toggleClientDatasetConfirmationModal>) {
    if (payload) {
        const allClients: Client[] = yield select(getAllClients);
        const allClientIds = allClients.map(({ clientId }) => clientId!);
        yield put(updateValue('clientIds', allClientIds));
    }
}

function* updateDocumentNameClientIdsWatcher() {
    yield takeLatest(DocumentNameActionTypes.TOGGLE_CLIENT_DATASET_CONFIRMATION_MODAL_OPEN, updateDocumentNameClientIds);
}

export function* attemptFetchAllDocumentGroups() {
    try {
        const documentGroups: ClientDocumentGroup[] = yield call(fetchAllDocumentGroups, {});
        yield put(fetchAllDocumentGroupsSuccessful(documentGroups));
    } catch (e) {
        yield put(fetchAllDocumentGroupsFailed((e as Error).message));
        toast.error('Unable to fetch the latest list of document groups. Please try again.');
    }
}

function* fetchAllDocumentGroupsWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_ALL_DOCUMENT_GROUPS_STARTED, attemptFetchAllDocumentGroups);
}

export function* attemptUpsertClientDocumentGroup() {
    try {
        const selectedDocumentGroup: ClientDocumentGroup = yield select(getSelectedDocumentGroup);
        yield call(upsertClientDocumentGroup, selectedDocumentGroup);
        yield put(saveClientDocumentGroupSuccessful());
        yield put(fetchAllDocumentGroupsStarted());
    } catch (e) {
        yield put(saveClientDocumentGroupFailed((e as Error).message));
        toast.error('Unable to save document group. Please try again.');
    }
}

function* upsertClientDocumentGroupWatcher() {
    yield takeLeading(DocumentNameActionTypes.SAVE_CLIENT_DOCUMENT_GROUP_STARTED, attemptUpsertClientDocumentGroup);
}

export function* attemptDeleteClientDocumentGroup() {
    try {
        const { clientDocumentGroupId } = yield select(getSelectedDocumentGroup);
        yield call(deleteClientDocumentGroup, { clientDocumentGroupId });
        yield put(deleteClientDocumentGroupSuccessful());
        yield put(fetchAllDocumentGroupsStarted());
    } catch (e) {
        yield put(deleteClientDocumentGroupFailed((e as Error).message));
        toast.error('Unable to delete document group. Please try again.');
    }
}

function* deleteClientDocumentGroupWatcher() {
    yield takeLeading(DocumentNameActionTypes.DELETE_CLIENT_DOCUMENT_GROUP_STARTED, attemptDeleteClientDocumentGroup);
}

export function* checkAgreementTypesUpdated() {
    const currentAgreementTypes: CurrentAgreementType[] = yield select(getCurrentAgreementTypes);
    const savedAgreementTypes: AgreementTypeDB[] = yield select(getAllAgreementTypes);
    const haveUpdated = !isEqual(currentAgreementTypes, savedAgreementTypes);
    yield put(setAgreementTypesHaveUpdated(haveUpdated));
}

function* checkAgreementTypesUpdatedWatcher() {
    yield debounce(200, [DocumentNameActionTypes.ADD_NEW_AGREEMENT_TYPE, DocumentNameActionTypes.REMOVE_NEW_AGREEMENT_TYPE, DocumentNameActionTypes.UPDATE_AGREEMENT_TYPE_NAME], checkAgreementTypesUpdated);
}

export function* attemptUpdateAgreementTypes() {
    try {
        const currentAgreementTypes: CurrentAgreementType[] = yield select(getCurrentAgreementTypes);
        const savedAgreementTypes: AgreementTypeDB[] = yield select(getAllAgreementTypes);
        const haveUpdated: boolean = yield select(getAgreementTypesHaveUpdated);
        if (haveUpdated) {
            const updatedAgreementTypes = currentAgreementTypes.filter((agreementType, currentIndex) => {
                const savedAgreementType = savedAgreementTypes.find((_, i) => i === currentIndex);
                return !isEqual(agreementType, savedAgreementType);
            });
            if (updatedAgreementTypes.length > 0) {
                const agreementTypes: AgreementTypeDB[] = yield call(updateAgreementTypes, { updatedAgreementTypes });
                yield put(updateAgreementTypesSuccessful(agreementTypes));
                toast('Successfully updated Agreement Types.');
            }
        }
    } catch (e) {
        yield put(updateAgreementTypesFailed((e as Error).message));
    }
}

function* updateAgreementTypesWatcher() {
    yield takeLeading(DocumentNameActionTypes.UPDATE_AGREEMENT_TYPES_STARTED, attemptUpdateAgreementTypes);
}

export function* attemptFetchMLReadyAgreementTypes() {
    try {
        const agreementTypeIds: number[] = yield call(fetchMLReadyAgreementTypeIds);
        yield put(fetchMLReadyAgreementTypeIdsSuccessful(agreementTypeIds));
    } catch (e) {
        yield put(fetchMLReadyAgreementTypeIdsFailed((e as Error).message));
    }
}

function* fetchMLReadyAgreementTypesWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_ML_READY_AGREEMENT_TYPES_STARTED, attemptFetchMLReadyAgreementTypes);
}

export function* attemptFetchAllAdminDocumentNames() {
    try {
        const documentNames: SystemDocumentNameDB[] = yield call(fetchAllAdminDocumentNames);
        yield put(fetchAllAdminDocumentNamesSuccessful(documentNames));
    } catch (e) {
        yield put(fetchAllAdminDocumentNamesFailed((e as Error).message));
        toast.error('Unable to fetch the latest list of document names. Please try again.');
    }
}

function* fetchAllAdminDocumentNamesWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_ALL_ADMIN_DOCUMENT_NAMES_STARTED, attemptFetchAllAdminDocumentNames);
}

export function* checkDocumentNameUpdated() {
    const currentSystemDocumentName: SystemDocumentNameDB = yield select(getCurrentDocumentName);
    const savedSystemDocumentName: SystemDocumentNameDB = yield select(getSavedDocumentName);
    const hasUpdated = !isEqual(currentSystemDocumentName, savedSystemDocumentName);
    yield put(setSystemDocumentNameUpdated(hasUpdated));
}

function* checkDocumentNameUpdatedWatcher() {
    yield debounce(200, [DocumentNameActionTypes.UPDATE_DOCUMENT_NAME_VALUE, DocumentNameActionTypes.ADD_NEW_DOCUMENT_NAME_AI_DISCOVER_ITEM, DocumentNameActionTypes.UPDATE_DOCUMENT_NAME_AI_DISCOVER_VALUE, DocumentNameActionTypes.DELETE_DOCUMENT_NAME_AI_DISCOVER_ITEM], checkDocumentNameUpdated);
}

export function* attemptFetchAllAgreementTypeRelationships() {
    try {
        const agreementTypeRelationships: AgreementTypeRelationship[] = yield call(fetchAllAgreementTypeRelationships);
        yield put(fetchAllAgreementTypeRelationshipsSuccessful(agreementTypeRelationships));
    } catch (e) {
        yield put(fetchAllAgreementTypeRelationshipsFailed((e as Error).message));
        toast.error('Unable to fetch the latest list of agreement type relationships. Please try again.');
    }
}

function* fetchAllAgreementTypeRelationshipsWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_ALL_AGREEMENT_TYPE_RELATIONSHIPS_STARTED, attemptFetchAllAgreementTypeRelationships);
}

export function* attemptFetchAgreementTypeHierarchy({ payload }: ReturnType<typeof fetchAgreementTypeHierarchyStarted>) {
    try {
        if (!isNull(payload)) {
            const agreementTypeHierarchy: AgreementTypeHierarchy = yield call(fetchAgreementTypeHierarchy, { agreementTypeId: payload });
            yield put(fetchAgreementTypeHierarchySuccessful(agreementTypeHierarchy));
        }
    } catch (e) {
        yield put(fetchAgreementTypeHierarchyFailed((e as Error).message));
        toast.error('Unable to fetch the latest agreement type hierarchy. Please try again.');
    }
}

function* attemptFetchAgreementTypeHierarchyWatcher() {
    yield takeLeading(DocumentNameActionTypes.FETCH_AGREEMENT_TYPE_HIERARCHY_STARTED, attemptFetchAgreementTypeHierarchy);
}

export function* redirectAgreementTypeRelationship({ payload }: ReturnType<typeof setAgreementRelationshipId>) {
    if (!isNull(payload)) {
        yield put(push(`/system/documents/agreement-relationships/${payload}`));
    } else {
        yield put(push('/system/documents/agreement-relationships'));
    }
}

function* redirectAIAgreementTypeRelationshipWatcher() {
    yield takeLatest(DocumentNameActionTypes.SET_AGREEMENT_RELATIONSHIP_ID, redirectAgreementTypeRelationship);
}

export function* redirectDocumentNamePageView({ payload }: ReturnType<typeof setDocumentNamePageView>) {
    const route = () => {
        switch (payload) {
            case DocumentNamePageView.FAMILY_TREE:
                return '/system/documents/agreement-relationships/family-tree';
            case DocumentNamePageView.RELATIONSHIPS:
                return '/system/documents/agreement-relationships';
            default:
                return '/system/documents';
        }
    };
    yield put(push(route()));
}

function* redirectDocumentNamePageViewWatcher() {
    yield takeLatest(DocumentNameActionTypes.SET_DOCUMENT_NAME_PAGE_VIEW, redirectDocumentNamePageView);
}

export function* attemptSaveAgreementTypeHierarchy() {
    try {
        const currentAgreementHierarchy: AgreementTypeHierarchy = yield select(getCurrentAgreementHierarchy);
        const parentAgreementTypeId: number = yield select(getSelectedAgreementTypeId);
        const newAgreementTypeRelationships: AgreementTypeRelationship[] = currentAgreementHierarchy[AgreementHierarchyRank.CHILDREN].map(({ agreementTypeId }) => ({ parentAgreementTypeId, childAgreementTypeId: agreementTypeId }));
        const { agreementTypeRelationships, agreementTypeHierarchy }: { agreementTypeRelationships: AgreementTypeRelationship[], agreementTypeHierarchy: AgreementTypeHierarchy } = yield call(upsertAgreementTypeHierarchy, { newAgreementTypeRelationships, parentAgreementTypeId });
        yield put(saveAgreementTypeHierarchySuccessful(agreementTypeRelationships, agreementTypeHierarchy));
    } catch (e) {
        yield put(saveAgreementTypeHierarchyFailed((e as Error).message));
        toast.error('Unable to save the Agreement type hierarchy. Please try again.');
    }
}

function* saveAgreementTypeHierarchyWatcher() {
    yield takeLeading(DocumentNameActionTypes.SAVE_AGREEMENT_TYPE_HIERARCHY_STARTED, attemptSaveAgreementTypeHierarchy);
}

export function* checkAgreementHierarchyCanSave() {
    const currentAgreementTypeHierarchy: AgreementTypeHierarchy = yield select(getCurrentAgreementHierarchy);
    const savedAgreementTypeHierarchy: AgreementTypeHierarchy = yield select(getSavedAgreementHierarchy);
    const currentChildIds = currentAgreementTypeHierarchy[AgreementHierarchyRank.CHILDREN].map(({ agreementTypeId }) => agreementTypeId);
    const savedChildIds = savedAgreementTypeHierarchy[AgreementHierarchyRank.CHILDREN].map(({ agreementTypeId }) => agreementTypeId);
    const hasUpdated = !isEqual(currentChildIds, savedChildIds) || savedChildIds.length !== currentChildIds.length;
    yield put(setCanSaveAgreementHierarchy(hasUpdated));
}

function* checkAgreementHierarchyCanSaveWatcher() {
    yield debounce(200, [DocumentNameActionTypes.UPDATE_AGREEMENT_HIERARCHY, DocumentNameActionTypes.SAVE_AGREEMENT_TYPE_HIERARCHY_SUCCESSFUL], checkAgreementHierarchyCanSave);
}

export function* attemptGenerateReport({ payload }: ReturnType<typeof generateReportStarted>) {
    try {
        const { agreementTypeId, excludedClientIds } = payload;
        const agreementTypes: AgreementTypeDB[] = yield select(getAllAgreementTypes);
        const agreementType = agreementTypes.find(agreementType => agreementType.agreementTypeId === agreementTypeId)?.name || '';
        const reportName = agreementType.length > 0 ? `${agreementType} (Deviated Fields Report)` : 'Deviated Fields Report';
        const report: Blob = yield call(createPopulatedFieldsReport, { agreementTypeId, excludedClientIds });
        yield call(download, report, reportName, MimeType.EXCEL);
        yield put(generateReportSuccessful());
    } catch (e) {
        yield put(generateReportFailed((e as Error).message));
        toast.error('Unable to generate report. Please try again.');
    }
}

function* generateReportWatcher() {
    yield takeLeading(DocumentNameActionTypes.GENERATE_REPORT_STARTED, attemptGenerateReport);
}

export function* documentNameSaga() {
    yield all([
        fork(updateDocumentNameWatcher),
        fork(fetchAllDocumentNamesWatcher),
        fork(fetchAvailableDocumentNamesWatcher),
        fork(updateDocumentNameClientIdsWatcher),
        fork(fetchAllDocumentGroupsWatcher),
        fork(upsertClientDocumentGroupWatcher),
        fork(deleteClientDocumentGroupWatcher),
        fork(fetchAllAgreementTypesWatcher),
        fork(checkAgreementTypesUpdatedWatcher),
        fork(updateAgreementTypesWatcher),
        fork(fetchMLReadyAgreementTypesWatcher),
        fork(fetchAllAdminDocumentNamesWatcher),
        fork(checkDocumentNameUpdatedWatcher),
        fork(fetchAllAgreementTypeRelationshipsWatcher),
        fork(redirectAIAgreementTypeRelationshipWatcher),
        fork(saveAgreementTypeHierarchyWatcher),
        fork(checkAgreementHierarchyCanSaveWatcher),
        fork(attemptFetchAgreementTypeHierarchyWatcher),
        fork(redirectDocumentNamePageViewWatcher),
        fork(generateReportWatcher)
    ]);
}
